import React, { type FC, type ReactNode, useRef, useState } from 'react';

import cn from 'classnames';

import { useCountdownTimer } from '@shared/hooks';
import { getFormatDate } from '@shared/lib/date';
import { isMedia } from '@shared/lib/html';
import { Button } from '@shared/ui/button';
import { ContactItem } from '@shared/ui/contact-item';
import { Label } from '@shared/ui/label';
import { Tooltip } from '@shared/ui/tooltip';
import IconInfo from '@assets/img/icons/info.svg';
import IconPhoneEnd from '@assets/img/icons/phone-end.svg';
import IconUserMany from '@assets/img/icons/user-many.svg';

import { formatCallStatus, getStateIcon } from '../lib/call-item';
import type { CallItemProps } from '../model/types';

import styles from './styles.module.scss';

const ACTIVE_CALL_STATES = new Set(['active', 'calling', 'incoming']);
const HOLD_CALL_STATES = new Set(['hold', 'remoteHold']);

/**
 * Компонент Contact отображает информацию о пользователе или группе, включая аватар, имя и дополнительное описание.
 */
export const CallItem: FC<CallItemProps> = ({
	user,
	onCall,
	onResume,
	onHangup,
	state,
	callInfo,
	callButtonText = 'Позвонить',
}) => {
	const [isHover, setIsHover] = useState(false);
	const [showTooltip, setShowTooltip] = useState(false);
	const refIconPending = useRef<HTMLSpanElement>(null);

	const callTimer = useCountdownTimer(callInfo?.startedAt);

	const isMissingCall = !callInfo?.endedAt;
	const isIncomingCall = callInfo?.direction === 'incoming';
	const isActiveCall = ACTIVE_CALL_STATES.has(state);
	const isHoldCall = HOLD_CALL_STATES.has(state);
	const isParkingCall = state === 'parking';
	const isParkedCall = state === 'parked';
	const isLocalHold = state === 'hold';
	const activeConferenceUsersLength =
		callInfo?.conferenceUsers?.filter(({ status }) => !['disconnected', 'pending'].includes(status))
			.length || 0;
	const isMobile = isMedia('sm');

	const contactItemClass = cn(styles.Container, {
		[styles.active]: isActiveCall,
		[styles.hover]: isHover,
	});

	const handleCall = () => onCall({ aor: user.aor, displayName: user.displayName });
	const handleResume = () => onResume?.(user.id);
	const handleHangup = () => onHangup?.(user.id);

	const handleMouseEnter = () => setIsHover(true);
	const handleMouseLeave = () => setIsHover(false);
	const handleTooltipOpen = () => setShowTooltip(true);
	const handleTooltipClose = () => setShowTooltip(false);

	const renderDirection = () => {
		if (isParkingCall) {
			return null;
		}

		if (isActiveCall) {
			return (
				<div className={styles.DirectionContainer}>
					{callTimer}

					{Boolean(activeConferenceUsersLength) && (
						<Label
							color="blueNeutral"
							tagClass={styles.DirectionContainer}
							text={
								<div className={styles.Label}>
									<span
										className={styles.DirectionIcon}
										dangerouslySetInnerHTML={{ __html: IconUserMany }}
									/>
									{activeConferenceUsersLength}
								</div>
							}
						/>
					)}
				</div>
			);
		}

		const icon = (
			<div
				className={styles.DirectionIcon}
				dangerouslySetInnerHTML={{ __html: getStateIcon(state, isIncomingCall) }}
			/>
		);

		if (isParkedCall) {
			return (
				<div className={styles.DirectionContainer}>
					{icon}
					<div className={styles.DirectionText}>Припаркован {callTimer}</div>
				</div>
			);
		}

		if (isHoldCall) {
			return (
				<div className={styles.DirectionContainer}>
					{icon}
					<div className={styles.DirectionText}>На удержании {callTimer}</div>
				</div>
			);
		}

		return (
			<div className={cn(styles.DirectionContainer, { [styles.missing]: isMissingCall })}>
				{icon}
				<div className={styles.DirectionText}>{isIncomingCall ? 'Входящий' : 'Исходящий'}</div>
			</div>
		);
	};

	const renderCallDetails = (short: boolean): ReactNode => {
		if (!callInfo?.startedAt || isParkedCall || isHoldCall) {
			return null;
		}

		if (isParkingCall) {
			return <div className={styles.CallText}>{callTimer}</div>;
		}

		return (
			<>
				<div className={styles.CallText}>{getFormatDate(callInfo.startedAt, short)}</div>
				<div className={styles.CallText}>{formatCallStatus(callInfo, short)}</div>
			</>
		);
	};

	const renderCallInfo = () => {
		if (isActiveCall) return null;

		const tooltipContent = renderCallDetails(false);

		if (isHover || isMobile) {
			return (
				<div className={styles.ButtonWrapper}>
					{isHoldCall || isParkedCall ? (
						<>
							{isLocalHold && (
								<Button className={styles.CallButton} onClick={handleResume}>
									Возобновить
								</Button>
							)}
							<Button
								buttonType="icon-primary"
								className={cn(styles.CallButton, styles.icon)}
								svg={IconPhoneEnd}
								onClick={handleHangup}
							/>
						</>
					) : (
						<>
							{!callInfo?.isConference && (
								<Button buttonType="primary" className={styles.CallButton} onClick={handleCall}>
									{callButtonText}
								</Button>
							)}
							{isMobile && tooltipContent && (
								<>
									<span
										ref={refIconPending}
										className={styles.TooltipIcon}
										dangerouslySetInnerHTML={{ __html: IconInfo }}
										onMouseEnter={handleTooltipOpen}
										onMouseLeave={handleTooltipClose}
									/>
									{showTooltip && (
										<Tooltip parent={refIconPending.current!} onClose={handleTooltipClose}>
											{tooltipContent}
										</Tooltip>
									)}
								</>
							)}
						</>
					)}
				</div>
			);
		}

		if (!isHoldCall || !isParkedCall) {
			return <div className={styles.CallInfo}>{renderCallDetails(true)}</div>;
		}
	};

	return (
		<div
			className={contactItemClass}
			role="button"
			tabIndex={0}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<ContactItem
				avatar={user.avatar}
				infoDescription={renderDirection()}
				isGroup={callInfo?.isConference}
				name={user.displayName || user.aor}
				status={user.status}
			/>

			{renderCallInfo()}
		</div>
	);
};
